import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "react-query";
import { renderApi, sitesApi } from "../services/api";
import { LayoutConfig, WakeSiteConfig } from "../types";
import { Settings, ChevronLeft, ChevronRight, Layers, Pin, PinOff, AlertCircle, X } from "lucide-react";
import toast from "react-hot-toast";
import ReactDOM from "react-dom";
import ComponentPanel from "../components/editor/ComponentPanel";
import PropertyPanel from "../components/editor/PropertyPanel";
import PreviewPanel from "../components/editor/PreviewPanel";
import PresenceToaster from "../components/editor/PresenceToaster";
import { useAuthStore } from "../stores/authStore";
import { useEditorStore } from "../stores/editorStore";

export default function EditorPage() {
  const { siteId, "*": pagePath } = useParams<{ siteId: string; "*": string }>();
  const page = pagePath || "home";
  const { token, useDevBranch } = useAuthStore();
  const initializeComponents = useEditorStore((state) => state.initializeComponents);

  // selectedComponent agora é gerenciado pelo editorStore
  const [layoutConfig, setLayoutConfig] = useState<LayoutConfig | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isComponentPanelOpen, setIsComponentPanelOpen] = useState(false);
  const [isPropertyPanelOpen, setIsPropertyPanelOpen] = useState(true);
  const [showBranchNotFoundModal, setShowBranchNotFoundModal] = useState(false);
  
  const [loadingStep, setLoadingStep] = useState<string>("Iniciando...");

  const componentPanelTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Usar o store do editor para detectar mudanças
  const { components, isDirty, lastSaved, selectedComponent } = useEditorStore();

  // Garantir que o setup seja chamado quando estiver em dev mode
  useEffect(() => {
    if (useDevBranch && siteId) {
      console.log(`🔧 EditorPage: Verificando se precisa fazer setup em dev mode para ${siteId}......`)
      const setupIfNeeded = async () => {
        try {
          const { authApi } = await import('../services/api')
          await authApi.setup(useDevBranch)
          console.log(`✅ Setup do repositório concluído para ${siteId} em dev mode`)
        } catch (setupError: any) {
          console.error('❌ Erro ao fazer setup do repositório:', setupError)
          if (setupError?.response?.data?.code === 'BRANCH_NOT_FOUND') {
            setShowBranchNotFoundModal(true)
          }
        }
      }
      setupIfNeeded()
    }
  }, [useDevBranch, siteId])

  // Buscar dados do site
  const { data: sitesData, isLoading: isLoadingSites } = useQuery("sites", sitesApi.list);
  const siteConfig = sitesData?.sites?.[0]; // Pega o primeiro site da lista

  // Carregar configuração do layout
  const { data: layoutData, isLoading: isLoadingLayout, error: layoutError } = useQuery(
    ["layout", siteId, page],
    () => renderApi.load(siteId!, page!),
    {
      enabled: !!siteId && !!page,
      onSuccess: (data) => {
        setLayoutConfig(data.layoutConfig);
        initializeComponents(data.layoutConfig, page);
        setLoadingStep("Configuração carregada");

        if (import.meta.env.DEV && data.storefrontPort) {
          try {
            const raw = localStorage.getItem('site-config');
            const cfg = raw ? JSON.parse(raw) : {};
            cfg.previewBaseUrl = `https://localhost:${data.storefrontPort}`;
            localStorage.setItem('site-config', JSON.stringify(cfg));
          } catch {}
        }
      },
      onError: (error: any) => {
        console.log('Erro ao carregar layout:', error);
        // Verificar se é erro de branch não encontrada e se está em dev mode
        const errorCode = error?.response?.data?.code || error?.code;
        const isBranchNotFound = errorCode === 'BRANCH_NOT_FOUND' || 
                                 error?.response?.status === 404 && error?.response?.data?.code === 'BRANCH_NOT_FOUND';
        
        if (isBranchNotFound && useDevBranch) {
          setShowBranchNotFoundModal(true);
        } else {
          setLoadingStep("Erro ao carregar configuração");
          toast.error(error?.response?.data?.error || "Erro ao carregar configuração");
        }
      }
    }
  );

  // LOG PARA VERIFICAR O ESTADO LOCAL
  /* useEffect(() => {
    console.log('[EditorPage] Estado local layoutConfig mudou:', layoutConfig);
  }, [layoutConfig]); */

  /* console.log('[EditorPage] Renderizando...', { 
    isLoadingLayout, 
    isLoadingSites, 
    isPreferencesLoaded,
    sitesData,
    siteConfig,
    siteId 
  }); */

  // Atualizar preview quando componentes mudarem
  useEffect(() => {
    if (lastSaved && !isDirty) {
      console.log('🔄 Componentes atualizados, preview atualiza automaticamente via iframe');
    }
  }, [lastSaved, isDirty]);

  // Cleanup do timeout quando componente for desmontado
  useEffect(() => {
    return () => {
      if (componentPanelTimeoutRef.current) {
        clearTimeout(componentPanelTimeoutRef.current);
      }
    };
  }, []);

  // Renderizar preview com retry automático
  // Preview agora é gerenciado pelo PreviewPanel via iframe direto

  // Publicar configuração - agora gerenciado pelo editorStore
  const handlePublish = async () => {
    if (!siteConfig || !layoutConfig) return;

    try {
      // O editorStore já gerencia o salvamento via updateEditingJson + apply
      toast.success("Configuração publicada com sucesso!");
    } catch (error) {
      toast.error("Erro ao publicar configuração");
    }
  };

  // Resetar JSON de edição para o estado original
  const handleReset = async () => {
    try {
      console.log('🔄 Iniciando reset...');
      
      setIsPreviewLoading(true);
      setLoadingStep('Resetando JSON de edição...');
      
      // Chamar API de reset para resetar o JSON
      await renderApi.reset();
      
      // Preview atualiza automaticamente via iframe
      setLoadingStep('JSON resetado com sucesso');
    } catch (error) {
      console.error('Erro ao resetar JSON:', error);
      setIsPreviewLoading(false);
      setLoadingStep('');
    }
  };

  useEffect(() => {
    if (layoutConfig) {
      // Preview atualiza automaticamente via iframe
    }
  }, [layoutConfig]);

  if (isLoadingLayout || isLoadingSites) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Carregando editor...
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            {isLoadingSites ? "Carregando dados do site..." : 
             loadingStep}
          </p>
          <div className="w-64 bg-gray-200 rounded-full h-2">
            <div className="bg-primary-600 h-2 rounded-full animate-pulse" style={{ width: '60%' }}></div>
          </div>
        </div>
      </div>
    );
  }

  if (!siteConfig) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900">
          Site não encontrado
        </h3>
        <p className="text-gray-500">O site especificado não foi encontrado.</p>
      </div>
    );
  }

  // Modal de Branch Não Encontrada
  const BranchNotFoundModal = () => {
    if (!showBranchNotFoundModal) return null;

    return ReactDOM.createPortal(
      <div className="modal-backdrop flex items-center justify-center z-[9999]">
        <div className="glass-modal modal-enter rounded-xl p-6 max-w-md w-full mx-4">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <AlertCircle className="w-6 h-6 text-blue-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900">
                Branch não encontrada
              </h3>
            </div>
            <button
              onClick={() => setShowBranchNotFoundModal(false)}
              className="p-2 hover:bg-gray-100 rounded-md transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          
          <div className="mb-4">
            <p className="text-gray-700 mb-2">
              A branch <strong>"site-editor"</strong> não foi encontrada no repositório.
            </p>
            <p className="text-gray-600 text-sm">
              Você está acessando o editor em modo de desenvolvimento, mas a branch <strong>site-editor</strong> não existe no repositório Git desta loja.
            </p>
          </div>

          <div className="flex space-x-3">
            <button
              onClick={() => setShowBranchNotFoundModal(false)}
              className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
            >
              Fechar
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  return (
    <>
      <BranchNotFoundModal />
      <PresenceToaster />
      <div className="h-full flex flex-col overflow-x-hidden">
      {/* Área Principal */}
      <div className="flex-1 relative flex overflow-hidden overflow-x-hidden">
        {/* Painel de Componentes - esquerda */}
        <div 
          className={`absolute z-10 flex-shrink-0 h-full glass-panel transition-[width] duration-300 ${
            isComponentPanelOpen ? 'w-60' : 'w-8'
          }`}
          onMouseEnter={() => {
            // Limpar timeout anterior se existir
            if (componentPanelTimeoutRef.current) {
              clearTimeout(componentPanelTimeoutRef.current);
            }
            // Definir timeout de 1 segundo para abrir
            componentPanelTimeoutRef.current = setTimeout(() => {
              setIsComponentPanelOpen(true);
            }, 200);
          }}
          onMouseLeave={() => {
            // Limpar timeout se o mouse sair antes de 1 segundo
            if (componentPanelTimeoutRef.current) {
              clearTimeout(componentPanelTimeoutRef.current);
              componentPanelTimeoutRef.current = null;
            }
            // Fechar imediatamente quando o mouse sair
            setIsComponentPanelOpen(false);
          }}
          >
            {/* Mantém o painel sempre montado; apenas oculta quando fechado */}
            <div className={`${isComponentPanelOpen ? 'block' : 'hidden'} h-full`}>
              <ComponentPanel
                layoutConfig={layoutConfig}
                onClose={() => {
                  setIsComponentPanelOpen(false);
                }}
              />
            </div>
            {!isComponentPanelOpen && (
              <div className="h-full flex flex-col items-center justify-center space-y-2">
                <div className="transform -rotate-90 flex items-center space-x-1 bg-gray-100 hover:bg-gray-200 rounded-r-lg px-2 py-1 transition-colors cursor-pointer">
                  <Layers className="w-4 h-4 text-gray-600" />
                  <span className="text-xs font-medium text-gray-600 whitespace-nowrap">
                    Componentes
                  </span>
                </div>
                <button
                  onClick={() => {
                    setIsComponentPanelOpen(true);
                  }}
                  className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                  title="Abrir painel de componentes"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>

        {/* Preview - ocupa o espaço restante */}
        <div className="flex-1 bg-gray-50 min-w-0 overflow-hidden">
          <PreviewPanel 
            isLoading={isPreviewLoading}
            loadingStep={loadingStep}
            onSave={handlePublish}
            onReset={handleReset}
            siteConfig={siteConfig}
            page={page}
          />
        </div>

        {/* Painel de Propriedades - direita */}
        <div
          className={`flex-shrink-0 h-full glass-panel transition-[width] duration-300 ${
            isPropertyPanelOpen ? "w-[250px]" : "w-8"
          }`}
        >
          <div className={`${isPropertyPanelOpen ? "block" : "hidden"} h-full`}>
            <PropertyPanel onToggleSidebar={() => setIsPropertyPanelOpen(false)} />
          </div>
          {!isPropertyPanelOpen && (
            <button
              onClick={() => setIsPropertyPanelOpen(true)}
              className="h-full w-full flex flex-col items-center justify-center space-y-2 hover:bg-gray-50 transition-colors cursor-pointer"
              title="Abrir painel de propriedades"
            >
              <div className="transform rotate-90 flex items-center space-x-1 bg-gray-100 rounded-r-lg px-2 py-1">
                <Settings className="w-4 h-4 text-gray-600" />
                <span className="text-xs font-medium text-gray-600 whitespace-nowrap">
                  Propriedades
                </span>
              </div>
              <div className="p-1 rounded bg-gray-100 text-gray-500">
                <ChevronLeft className="w-4 h-4" />
              </div>
            </button>
          )}
        </div>
      </div>
    </div>
    </>
  );
}
